import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState } from "@/components/Common";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "Tümü" },
  { key: "trial", label: "Deneme Süresinde" },
  { key: "trial_expired", label: "Deneme Süresi Dolmuş" },
  { key: "internal_free", label: "Dahili / Ücretsiz" },
  { key: "paid", label: "Ücretli / Aktif" },
  { key: "payment_pending", label: "Ödeme Bekleniyor" },
  { key: "overdue", label: "Gecikmiş" },
  { key: "passive", label: "Pasif" },
];

const CONSENT_FILTERS = [
  { key: "all", label: "Tüm İletişim İzinleri" },
  { key: "any", label: "Herhangi bir kanala izin verenler" },
  { key: "email", label: "E-posta izni olanlar" },
  { key: "sms", label: "SMS izni olanlar" },
  { key: "whatsapp", label: "WhatsApp izni olanlar" },
  { key: "phone", label: "Telefon araması izni olanlar" },
  { key: "none", label: "Hiçbir iletişim izni olmayanlar" },
];

const CONSENT_CHANNELS = [
  { key: "email", short: "E-posta", col: "E-posta İzni" },
  { key: "sms", short: "SMS", col: "SMS İzni" },
  { key: "whatsapp", short: "WhatsApp", col: "WhatsApp İzni" },
  { key: "phone", short: "Arama", col: "Telefon Araması İzni" },
];

export function ConsentCell({ cc, testId }) {
  return (
    <div className="flex items-center gap-1.5 text-xs whitespace-nowrap" data-testid={testId}>
      {CONSENT_CHANNELS.map(({ key, short }, i) => (
        <span key={key} className="inline-flex items-center gap-0.5">
          {i > 0 && <span className="text-slate-300 mr-1">/</span>}
          <span className="text-slate-600">{short}</span>
          <span className={cc?.[key] ? "text-emerald-600 font-semibold" : "text-rose-500 font-semibold"}>
            {cc?.[key] ? "✓" : "✕"}
          </span>
        </span>
      ))}
    </div>
  );
}

// Turkish user-facing labels for backend subscription_status values.
const SUB_LABEL = {
  trial: { text: "Deneme", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  trial_expired: { text: "Deneme Süresi Doldu", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  internal_free: { text: "Dahili / Ücretsiz", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  active: { text: "Ücretli / Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  paid: { text: "Ücretli / Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  payment_pending: { text: "Ödeme Bekleniyor", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  overdue: { text: "Gecikmiş", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  cancelled: { text: "İptal", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};
const ACC_LABEL = {
  active: { text: "Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  passive: { text: "Pasif", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  inactive: { text: "Pasif", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  archived: { text: "Arşiv", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  deleted: { text: "Silinmiş", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

export function Badge({ tone, fallback = "—" }) {
  if (!tone) return <span className="text-slate-400">{fallback}</span>;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${tone.cls}`}>
      {tone.text}
    </span>
  );
}

export const SUBSCRIPTION_LABEL = SUB_LABEL;
export const ACCOUNT_LABEL = ACC_LABEL;

// Kayıt kaynağı sunum sözlüğü — backend'de lower-case normalize edilir;
// bilinen kanallar için Türkçe kısa etiket, bilinmeyenler için ham değer
// gösterilir.  Boş / eksik değerler ekran "—" gösterir.
export const ACQUISITION_SOURCE_LABEL = {
  instagram: "Instagram",
  linkedin:  "LinkedIn",
  google:    "Google Ads",
  google_ads:"Google Ads",
  facebook:  "Facebook",
  direct:    "Direct",
  email:     "E-posta",
  referral:  "Referans",
  organic:   "Organik",
};
export function fmtAcquisitionSource(src) {
  if (!src) return "—";
  const key = String(src).trim().toLowerCase();
  if (ACQUISITION_SOURCE_LABEL[key]) return ACQUISITION_SOURCE_LABEL[key];
  // Bilinmeyen kaynak: ilk harfi büyük göster
  return key.charAt(0).toUpperCase() + key.slice(1);
}

// Kanal / Kampanya için snake_case veya kebab-case değerleri
// "Direct Sales" gibi Title Case sunuma dönüştürür.  Boş/undefined ise
// "—" döner.  Kaynak (source) için ayrı bir sözlük vardır (yukarıda).
export function fmtAcquisitionText(v) {
  if (!v) return "—";
  const s = String(v).trim().replace(/[_-]+/g, " ");
  return s.split(/\s+/).map(w => w.charAt(0).toLocaleUpperCase("tr-TR") + w.slice(1)).join(" ");
}

const d10 = (v) => (v ? fmtDate(v.slice(0, 10)) : "—");

function remainingLabel(r) {
  if (r.subscription_status === "internal_free") return "Sınırsız";
  if (r.subscription_status === "trial_expired") return "Deneme Süresi Doldu";
  return r.days_left == null ? "—" : `${r.days_left} gün`;
}

function toExportRow(r) {
  const free = r.subscription_status === "internal_free";
  return {
    "Firma": r.name || "—",
    "Kullanıcı Adı": r.owner?.username || "—",
    "Yetkili": r.owner?.full_name || "—",
    "E-posta": r.owner?.email || "—",
    "Telefon": r.owner?.phone || "—",
    "Kayıt": d10(r.created_at),
    "Kayıt Kaynağı": fmtAcquisitionSource(r.acquisition?.source),
    "Son Giriş": r.owner?.last_login_at ? fmtDateTime(r.owner.last_login_at) : "—",
    "Son Aktivite": r.activity?.last_activity_at ? fmtDateTime(r.activity.last_activity_at) : "—",
    "Deneme Bitiş": free ? "—" : d10(r.trial_end_date),
    "Kalan": remainingLabel(r),
    "Abonelik": SUB_LABEL[r.subscription_status]?.text || "—",
    "Hesap": ACC_LABEL[r.account_status]?.text || "—",
    "Son Ödeme": d10(r.last_payment_date),
    "Sonraki Ödeme": free ? "—" : d10(r.next_payment_date),
    ...Object.fromEntries(CONSENT_CHANNELS.map(({ key, col }) => [col, r.contact_consent?.[key] ? "Evet" : "Hayır"])),
    "İzin Tarihi": r.contact_consent?.updated_at ? fmtDateTime(r.contact_consent.updated_at) : "—",
  };
}

export default function AdminCompanies() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [consent, setConsent] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (filter !== "all") params.filter = filter;
      if (consent !== "all") params.consent = consent;
      const { data } = await api.get("/admin/companies", { params });
      setRows(data || []);
    } catch (e) {
      toast.error("Firmalar yüklenemedi");
    } finally { setLoading(false); }
  }, [search, filter, consent]);

  useEffect(() => { load(); }, [filter, consent, load]);

  const onSearchKey = (e) => { if (e.key === "Enter") load(); };

  const list = useMemo(() => rows || [], [rows]);
  const [exporting, setExporting] = useState(null);

  const exportExcel = async (kind) => {
    setExporting(kind);
    const params = { filter: "all_records", ...(kind === "consented" ? { consent: "any" } : {}) };
    const prefix = kind === "consented" ? "penz-firmalar-izinli" : "penz-firmalar";
    try {
      const [{ data }, XLSX] = await Promise.all([api.get("/admin/companies", { params }), import("xlsx")]);
      const XL = XLSX.utils ? XLSX : XLSX.default;
      const ws = XL.utils.json_to_sheet((data || []).map(toExportRow));
      const wb = XL.utils.book_new();
      XL.utils.book_append_sheet(wb, ws, "Firmalar");
      XL.writeFile(wb, `${prefix}-${new Date().toLocaleDateString("sv-SE")}.xlsx`);
    } catch (e) {
      toast.error("Excel dosyası oluşturulamadı");
    } finally { setExporting(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-companies">
      <PageHeader
        title="Firmalar"
        subtitle="Penz'e kayıt olan tüm firmalar"
        action={
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => exportExcel("consented")}
              disabled={!!exporting}
              className="h-10 rounded-lg gap-2"
              data-testid="admin-export-consented-excel-btn"
            >
              {exporting === "consented" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              İzinlileri Excel'e Aktar
            </Button>
            <Button
              variant="outline"
              onClick={() => exportExcel("all")}
              disabled={!!exporting}
              className="h-10 rounded-lg gap-2"
              data-testid="admin-export-excel-btn"
            >
              {exporting === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Excel'e Aktar
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onSearchKey}
            placeholder="Firma, yetkili, e-posta veya telefon ara... (Enter)"
            className="pl-9 h-10 rounded-lg"
            data-testid="admin-search-input"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-64 h-10 rounded-lg" data-testid="admin-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {FILTERS.map((f) => <SelectItem key={f.key} value={f.key} data-testid={`admin-filter-option-${f.key}`}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={consent} onValueChange={setConsent}>
          <SelectTrigger className="w-72 h-10 rounded-lg" data-testid="admin-consent-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CONSENT_FILTERS.map((f) => <SelectItem key={f.key} value={f.key} data-testid={`admin-consent-option-${f.key}`}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <SectionCard>
        {loading ? (
          <div className="p-5 text-sm text-slate-500">Yükleniyor...</div>
        ) : list.length === 0 ? (
          <EmptyState title="Firma bulunamadı" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Firma</th>
                  <th className="px-5 py-3 font-semibold">Yetkili</th>
                  <th className="px-5 py-3 font-semibold">E-posta</th>
                  <th className="px-5 py-3 font-semibold">Telefon</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Kayıt</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Kayıt Kaynağı</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Son Giriş</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Son Aktivite</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Deneme Bitiş</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Kalan</th>
                  <th className="px-5 py-3 font-semibold">Abonelik</th>
                  <th className="px-5 py-3 font-semibold">Hesap</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Son Ödeme</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Sonraki Ödeme</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">İletişim İzinleri</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((r) => {
                  const days = r.days_left;
                  const isInternalFree = r.subscription_status === "internal_free";
                  const remainingText = isInternalFree
                    ? "Sınırsız"
                    : r.subscription_status === "trial_expired"
                      ? "Deneme Süresi Doldu"
                      : (days == null ? "—" : `${days} gün`);
                  const remainingCls = isInternalFree
                    ? "text-indigo-700"
                    : r.subscription_status === "trial_expired"
                      ? "text-rose-600"
                      : (days != null && days <= 2 ? "text-amber-600" : "text-slate-700");
                  return (
                    <tr
                      key={r.id}
                      className="hover:bg-slate-50/60 cursor-pointer"
                      onClick={() => navigate(`/admin/companies/${r.id}`)}
                      data-testid={`admin-company-row-${r.id}`}
                    >
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-900">{r.name || "—"}</div>
                        <div className="text-xs text-slate-500">{r.owner?.username || ""}</div>
                      </td>
                      <td className="px-5 py-3">{r.owner?.full_name || "—"}</td>
                      <td className="px-5 py-3 text-slate-700 truncate max-w-[220px]">{r.owner?.email || "—"}</td>
                      <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{r.owner?.phone || "—"}</td>
                      <td className="px-5 py-3 whitespace-nowrap">{r.created_at ? fmtDate(r.created_at.slice(0, 10)) : "—"}</td>
                      <td
                        className="px-5 py-3 whitespace-nowrap text-slate-700"
                        data-testid={`admin-acquisition-source-${r.id}`}
                      >
                        {fmtAcquisitionSource(r.acquisition?.source)}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-slate-700" data-testid={`admin-last-login-${r.id}`}>
                        {r.owner?.last_login_at ? fmtDateTime(r.owner.last_login_at) : "—"}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-slate-700" data-testid={`admin-last-activity-${r.id}`}>
                        {r.activity?.last_activity_at ? fmtDateTime(r.activity.last_activity_at) : "—"}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {isInternalFree ? "—" : (r.trial_end_date ? fmtDate(r.trial_end_date.slice(0, 10)) : "—")}
                      </td>
                      <td className={`px-5 py-3 whitespace-nowrap font-medium ${remainingCls}`}>{remainingText}</td>
                      <td className="px-5 py-3"><Badge tone={SUB_LABEL[r.subscription_status]} /></td>
                      <td className="px-5 py-3"><Badge tone={ACC_LABEL[r.account_status]} /></td>
                      <td className="px-5 py-3 whitespace-nowrap text-slate-700">
                        {r.last_payment_date ? fmtDate(r.last_payment_date.slice(0, 10)) : "—"}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-slate-700">
                        {isInternalFree ? "—" : (r.next_payment_date ? fmtDate(r.next_payment_date.slice(0, 10)) : "—")}
                      </td>
                      <td className="px-5 py-3">
                        <ConsentCell cc={r.contact_consent} testId={`admin-consent-${r.id}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
